import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import _ from "lodash";
import { Observable, Subject, of, takeUntil } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { Mottagningsplatstyper } from "~app/common/models/constants";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { TypeaheadService } from "~app/common/services/typeahead/typeahead.service";
import { TypeAhead } from "~app/common/services/typeahead/typeahead.types";
import { Utils } from "~app/common/services/utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ILagerplats, IMottagningsplats, ISDCLagerplats, ISDCMottagningsplats } from "../../mottagningsplats.models";
import { MottagningsplatsService } from "../../mottagningsplats.service";
import { MottagningsplatsDetaljerTexts } from "../mottagningsplats-detaljer.texts";
import { MottagningsplatsDetaljerPlatsValidation } from "../mottagningsplats-detaljer.validation";

@Component({
    selector: "rask-mottagningsplats-detaljer-header-sv",
    templateUrl: "./mottagningsplats-detaljer-header-sv.component.html",
    styleUrls: ["./mottagningsplats-detaljer-header-sv.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        CheckboxComponent,
        ButtonComponent,
        TextareaComponent,
        InputNumberComponent
    ]
})
export class MottagningsplatsDetaljerHeaderSvComponent implements OnInit, OnDestroy, OnChanges {
    form: UntypedFormGroup;
    initialized = false;
    @Input() isNew: boolean;
    @Output() lagerplatserChanged: EventEmitter<ILagerplats[]> = new EventEmitter<ILagerplats[]>();
    @Input() mottagningsplats: IMottagningsplats;
    mottagningsplatsTyper: string[];
    @Input() rootForm: UntypedFormGroup;
    texts = MottagningsplatsDetaljerTexts.texts;
    validation: IValidationScope;
    validationContext: IValidationContext;
    private readonly busyService = inject(BusyService);
    private readonly formBinder = inject(FormBinderService);
    private readonly mottagningsplatsService = inject(MottagningsplatsService);
    private readonly ngFormSubscription$ = new Subject<void>();
    private readonly typeaheadService = inject(TypeaheadService);

    aktivMetodDisabled = (l) => !l.aktiv;

    isEgenTerminalMottagningsplats(): boolean {
        return this.form.get("egenTerminal").value;
    }

    ngOnChanges() {
        this.setUpForm();
    }

    ngOnDestroy(): void {
        this.formBinder.detachFromRootForm(this.rootForm, `mottagningsplatssv${this.mottagningsplats.id}`);
        this.ngFormSubscription$?.next();
        this.ngFormSubscription$?.complete();
    }

    ngOnInit(): void {
        this.mottagningsplatsTyper = Mottagningsplatstyper.Alla;
        this.setUpForm();
    }

    sdcMottagningsplatsViewFormatter = (item) => item.kod ? `${item.kod}-${item.namn}` : item.namn;

    searchLogistikerTypeAhead = (input: string) => input.length < 3
        ? this.getPreload(TypeAhead.AnsvarigLogistiker)
        : this.mottagningsplatsService.searchForAnsvarigLogistikerTypeahead(input);

    searchSDCMottagningsplatsByKod = (input: string) => input.length < 3
        ? this.getPreload(TypeAhead.SDCMottagningsplats)
        : this.mottagningsplatsService.searchForSDCMottagningsplatsTypeAhead(input);

    searchTerminalCkLeverantorTypeAhead = (input: string) => input.length < 3
        ? this.getPreload(TypeAhead.TerminalCkLeverantor)
        : this.mottagningsplatsService.searchForLeverantorTerminalByNamnHkInternnrTypeahead(input);

    searchTerminalKundTypeAhead = (input: string) => input.length < 3
        ? this.getPreload(TypeAhead.TerminalKund)
        : this.mottagningsplatsService.searchForSveaskogKundNamnHkInternnummerTypeAhead(input);

    setUpForm() {
        this.ngFormSubscription$?.next();

        this.form = this.formBinder.CreateGroup(this.mottagningsplats);
        this.validationContext = MottagningsplatsDetaljerPlatsValidation.initHeaderSv(this.mottagningsplats);
        this.validationContext.scope(ValidationScopes.spara)
            .pipe(takeUntil(this.ngFormSubscription$))
            .subscribe(v => {
                this.validation = v;
            });

        this.validationContext.validate();
        this.formBinder.attachValidation(this.validationContext.current, this.form, true);
        this.initialized = true;
        this.formBinder.attachToRootForm(this.rootForm, this.form, `mottagningsplatssv${this.mottagningsplats.id}`).subscribe(x => {
            this.rootForm.addControl(`mottagningsplatssv${this.mottagningsplats.id}`, this.form);
        });

        this.form.get("mottagningsplatsnummer").valueChanges
            .pipe(takeUntil(this.ngFormSubscription$))
            .subscribe((mplNummer: string) => {
                this.syncMottagningsplats(mplNummer);
            });

        this.form.get("egenTerminal").valueChanges
            .pipe(takeUntil(this.ngFormSubscription$))
            .subscribe((newValue: boolean) => {
                this.mottagningsplats.egenTerminal = newValue;
                this.validationContext.validate();
                this.formBinder.updateErrors(this.validationContext.current, this.form);
            });
    }

    syncAvlastningsplats(sdcLagerplatser: ISDCLagerplats[]) {
        const diffResults = Utils.diff(this.mottagningsplats.lagerplatser, sdcLagerplatser, a => a.avlamningsplats, b => b.kod);

        let platser = [];
        diffResults.adds.forEach(x => {
            platser.push({
                id: null,
                avlamningsplats: x.kod,
                namn: x.namn,
                koordinatN: x.northing,
                koordinatE: x.easting,
                matmetoder: [],
                mottagningsplatsKod: this.mottagningsplats.mottagningsplatsnummer,
                oppettider: null,
                rowVersion: null,
                finnsIViol3: true,
                aktiv: true
            });
        });

        diffResults.matched.forEach(x => {
            // Klona för att ersätta referens
            const modified = _.clone(x.source);
            modified.koordinatE = x.other.easting;
            modified.koordinatN = x.other.northing;
            modified.namn = x.other.namn;
            modified.finnsIViol3 = true;
            platser.push(modified);
        });

        // Lägg endast till och uppdatera, ta ej bort
        diffResults.removes.forEach(x => platser.push(x));

        if (this.isNew) {
            const avlPlats0 = platser.find(x => x.avlamningsplats === "0");
            // Se till att 0an alltid finns men sätt "finnsIViol3" false
            // Om vi är i nytt läge så ser vi till att uppdatera platsen
            if (avlPlats0) {
                platser.splice(platser.indexOf(avlPlats0), 1);
            }

            platser.push({
                id: null,
                avlamningsplats: "0",
                namn: this.mottagningsplats.namn,
                koordinatN: this.mottagningsplats.northing,
                koordinatE: this.mottagningsplats.easting,
                matmetoder: [],
                mottagningsplatsKod: this.mottagningsplats.mottagningsplatsnummer,
                oppettider: null,
                rowVersion: null,
                finnsIViol3: false,
                aktiv: true
            });
        }

        platser = _.sortBy(platser, x => x.avlamningsplats);
        this.lagerplatserChanged.emit(platser);
    }

    syncMottagningsplats(mplNummer: string) {
        this.busyService
            .addWork(this.mottagningsplatsService.getSDCMottagningsplatsByKod(mplNummer))
            .subscribe(x => {
                if (x) {
                    this.updateMottagningsplats(x);
                }
            });
    }

    typeAheadValueFormatter(item) {
        return item;
    }

    typeAheadViewFormatter = (item) => item?.namn;

    typeAheadViewSelectedFormatter = (item) => item?.kod;

    updateMottagningsplats(x: ISDCMottagningsplats) {
        this.form.get("namn").setValue(x.namn);
        this.form.get("site").setValue(x.site);
        this.form.get("northing").setValue(x.northing);
        this.form.get("easting").setValue(x.easting);

        this.syncAvlastningsplats(x.lagerplatser);
        if (this.mottagningsplats.mottagningsplatsnummer.toLocaleUpperCase().endsWith("HA")) {
            // Slutar det på HA så är det en hämtplats.
            this.form.get("mottagningsplatstyp").setValue(Mottagningsplatstyper.Hamtplats);
        } else {
            this.form.get("mottagningsplatstyp").setValue(Mottagningsplatstyper.Mottagningsplats);
        }

        this.form.markAsDirty();
        this.validationContext.validate();
    }

    private getPreload<T>(type: string): Observable<T[]> {
        return of(this.typeaheadService.getPreload<T>(type, this.texts.typaheadefaulttext));
    }
}
